import React from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Link, Icon, LogoImage, AvatarImage } from '@hz-design-system/web-ui';

import { TListingClient, TListingSellerInfo } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

import SellerReviews from './SellerReviews';
import SellerExternalReviews from './SellerExternalReviews';
import SellerTrustIndicators from './SellerTrustIndicators';

import Classes from './SellerInfoSmall.scss';
import calculateActiveSinceDiff from '../../../../utils/calculateActiveSinceDiff';
import getFormattedLocation from '../../../../utils/getFormattedLocation';

type TSellerInfoProps = {
  seller: TListingSellerInfo;
  isAutomotiveAd: TListingClient['isAutomotiveAd'];
  isAutomotiveDealer: boolean;
  profile: TSellerProfileClient;
};

const SellerInfoSmall = ({ seller, isAutomotiveAd, isAutomotiveDealer, profile }: TSellerInfoProps) => {
  const { t } = useI18nContext();
  const { name, activeSince, location, pageUrl, showProfilePicture } = seller;
  const activeSinceDiff = calculateActiveSinceDiff(activeSince, t);
  const formattedLocation = getFormattedLocation(location, t);
  const isBusinessSeller = seller.sellerType === 'TRADER' || isAutomotiveDealer;
  const ImageComponent = isBusinessSeller ? LogoImage : AvatarImage;
  const placeholderIconName = isBusinessSeller ? 'shop' : 'profile';
  const imageSrc =
    profile.profilePictures?.logoPicture && showProfilePicture ? profile.profilePictures.logoPicture : '';

  // SMB verified does not show for cars, as automotive is not included in the SMB bundles
  // refer to BNL-7414
  const shouldShowSmbVerified = !isAutomotiveAd && Boolean(profile.smbVerified);

  return (
    <div className={Classes.root}>
      <div className={Classes.image}>
        <ImageComponent
          src={imageSrc}
          alt={`${name} ${t('sellerInfo.profilePictures.logoAltText')}`}
          placeholderIconName={placeholderIconName}
        />
      </div>
      <div className={Classes.section}>
        <div className={Classes.row}>
          <Link href={pageUrl}>{name}</Link>
          {shouldShowSmbVerified && (
            <Icon name="checkmark-circle" color="signal-success-default" title={t('sellerInfo.verifiedTooltip')} />
          )}
        </div>
        {isAutomotiveAd && <div className={Classes.row}>{formattedLocation}</div>}
        {(!isAutomotiveAd || !Boolean(profile.numberOfReviews)) && (
          <Trans className={Classes.row} i18nKey="sellerInfo.activeOnPlatform" activeSinceDiff={activeSinceDiff} />
        )}
        {Boolean(profile.numberOfReviews) && (
          <div className={Classes.row}>
            {!profile.externalSource && (
              <SellerReviews numberOfReviews={profile.numberOfReviews} averageScore={profile.averageScore} />
            )}
            {profile.externalSource && (
              <SellerExternalReviews
                sellerName={name}
                numberOfReviews={profile.numberOfReviews}
                averageScore={profile.averageScore}
                externalReviews={profile.externalReviews}
                provider={profile.provider}
                showProfilePicture={showProfilePicture}
                profilePictures={profile.profilePictures}
              />
            )}
          </div>
        )}
      </div>
      <SellerTrustIndicators verifications={profile} shouldShowSmbVerified={shouldShowSmbVerified} />
    </div>
  );
};

export default SellerInfoSmall;
